import React from "react";
import "./Movie.css";
import Wrapper from "../components/basic/wrapper";
import SafeArea from "../components/basic/safe-area";
import movie_afiche from "../assets/images/webp/movie_afiche.webp";
import PhotoLiner from "../components/basic/photo-liner/PhotoLiner";
import movieCreditsBg from "../assets/images/webp/movie_credits.webp";
import movieCreditsBgShadowed from "../assets/images/webp/movie_credits_shadowed.webp";
import Trailer from "../components/sections/movie/Trailer";
import PhotoLinerV2 from "../components/basic/photo-liner-v2/PhotoLinerV2";
import { useI18n } from "../i18n/i18n";

const Movie: React.FC = () => {
    const sectionColor = "#ffffff";
    const sectionTextColor = "#FFFFFF";
    const [currentIndexMobile, setCurrentIndexMobile] = React.useState(0);

    const { t } = useI18n();

    const trackRef = React.useRef<HTMLDivElement | null>(null);
    const slideRefs = React.useRef<(HTMLDivElement | null)[]>([]);
    const [containerHeight, setContainerHeight] = React.useState<
        number | undefined
    >(slideRefs.current[currentIndexMobile]?.offsetHeight);

    const touchStartXRef = React.useRef<number | null>(null);
    const touchStartYRef = React.useRef<number | null>(null);
    const containerWidthRef = React.useRef<number>(0);
    const [dragX, setDragX] = React.useState(0);
    const [isDragging, setIsDragging] = React.useState(false);
    const [isFichaOpen, setIsFichaOpen] = React.useState(false);
    const [isFicha2Open, setIsFicha2Open] = React.useState(false);

    const toggleFicha = () => setIsFichaOpen((prev) => !prev);
    const toggleFicha2 = () => setIsFicha2Open((prev) => !prev);

    // preload both backgrounds once
    const usePreloadImages = (urls: string[]) => {
        React.useEffect(() => {
            urls.forEach((url) => {
                if (!url) return;
                const img = new Image();
                img.src = url;
            });
        }, [urls]);
    };
    usePreloadImages([movieCreditsBg, movieCreditsBgShadowed]);

    // lock body scroll when ficha is open
    React.useEffect(() => {
        document.body.style.overflow = isFichaOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [isFichaOpen]);

    const SWIPE_THRESHOLD_RATIO = 0.2; // 20% of width

    // track translate
    const baseTranslatePercent = -currentIndexMobile * 100;
    const dragPercent =
        containerWidthRef.current > 0 ? (dragX / containerWidthRef.current) * 100 : 0;
    const trackTranslate = baseTranslatePercent + dragPercent;

    React.useEffect(() => {
        const el = slideRefs.current[currentIndexMobile];
        if (el) setContainerHeight(el.offsetHeight);
    }, [currentIndexMobile]);

    // optional: recalc on resize
    React.useEffect(() => {
        const handleResize = () => {
            const el = slideRefs.current[currentIndexMobile];
            if (el) setContainerHeight(el.offsetHeight);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [currentIndexMobile]);

    return (
        <>
            <Wrapper
                color={sectionColor}
                footerColor={sectionTextColor}
                logoColor={sectionTextColor}
            >
                <>
                    <div style={{ backgroundColor: sectionColor }}>
                        <Trailer scrollTargetId="movie-scroll-target" />
                    </div>

                    <div className="movie-sinopsis-layout">
                        <div className="flex1" style={{ background: "#f5f5f5" }}>
                            <SafeArea>
                                <div className="movie-hm" id="movie-scroll-target">
                                    <img src={movie_afiche} alt="" />
                                </div>
                            </SafeArea>
                        </div>

                        {/* Desktop synopsis */}
                        <div className="movie-sipnosis-wrapper-desktop flex1">
                            <SafeArea fullWidth={false}>
                                <>
                                    {/* Keep your custom title style */}
                                    <div
                                        className="ln26 mb28 w100 cormorant fs14 up"
                                        style={{ letterSpacing: "8%" }}
                                    >
                                        {t("sections.synopsisFilmTitle")}
                                    </div>

                                    <div className="movie-sipnosis mb25">
                                        {t("movie.synopsis.p1")}
                                    </div>
                                    <div className="movie-sipnosis mb25">
                                        {t("movie.synopsis.p2")}
                                    </div>
                                    <div className="movie-sipnosis">{t("movie.synopsis.p3")}</div>
                                </>
                            </SafeArea>
                        </div>

                        {/* Mobile synopsis */}
                        <div className="movie-sipnosis-wrapper-mobile flex1">
                            <SafeArea>
                                <>
                                    <PhotoLiner
                                        title={t("sections.synopsisFilmTitle")}
                                        color="#000000"
                                        showLine
                                    />
                                    <div className="movie-sipnosis mb25">{t("movie.synopsis.p1")}</div>
                                    <div className="movie-sipnosis mb25">{t("movie.synopsis.p2")}</div>
                                    <div className="movie-sipnosis">{t("movie.synopsis.p3")}</div>
                                </>
                            </SafeArea>
                        </div>
                    </div>

                    {/* Director note - Desktop */}
                    <div className="movie-note-desktop">
                        <PhotoLinerV2
                            title={t("movie.directorsNote.title")}
                            color="#ffffff"
                            customClass="fs32 fw700"
                            absolute
                            onHoverChange={toggleFicha}
                            hovered={isFichaOpen}
                        />
                        <div className={!isFichaOpen ? "hidden" : "visible"}>
                            <SafeArea>
                                <div className="d-flex jcsb aic w100">
                                    <div className="movie-note-p1">
                                        <div
                                            className="fs16 ln26"
                                            style={{ maxHeight: "750px", overflowY: "scroll" }}
                                        >
                                            <div>{t("movie.directorsNote.p1")}</div>
                                            <br />
                                            <div>{t("movie.directorsNote.p2")}</div>
                                            <br />
                                            <div>{t("movie.directorsNote.p3")}</div>
                                        </div>
                                    </div>
                                    <div className="movie-note-p2" />
                                </div>
                            </SafeArea>
                        </div>
                    </div>

                    {/* Director note - Mobile */}
                    <div className="movie-note-mobile">
                        <PhotoLinerV2
                            title={t("movie.directorsNote.title")}
                            color="#ffffff"
                            customClass="fs32 fw700"
                            hovered
                        />
                        <SafeArea>
                            <>
                                <div className="fs16 ln26 mt40">
                                    <div>{t("movie.directorsNote.p1")}</div>
                                    <br />
                                    <div>{t("movie.directorsNote.p2")}</div>
                                    <br />
                                    <div>{t("movie.directorsNote.p3")}</div>
                                </div>
                            </>
                        </SafeArea>
                        <img
                            className="objcov"
                            src="https://images-extras.s3.us-east-1.amazonaws.com/movie_note_mobile.png"
                            alt=""
                        />
                    </div>

                    {/* Credits - Desktop */}
                    <div
                        className={`movie-credits-desktop ${isFicha2Open ? "is-open" : ""}`}
                        style={{
                            backgroundImage: `url(${isFicha2Open ? movieCreditsBgShadowed : movieCreditsBg
                                })`,
                        }}
                    >
                        <PhotoLinerV2
                            title={t("sections.creditsTitle")}
                            color="#ffffff"
                            customClass="fs32 fw700"
                            absolute
                            onHoverChange={toggleFicha2}
                            hovered={isFicha2Open}
                        />
                        <div
                            className={
                                "project-ficha-artistica-overlay" + (isFicha2Open ? " is-visible" : "")
                            }
                        >
                            <div className="project-ficha-artistica-overlay-inner d-flex flex-col ln26 fw900 tac">
                                <span className="credit-row-left">
                                    {t("movie.credits.withParticipation")}
                                </span>
                                <span className="credit-row-right">{t("movie.credits.cast")}</span>

                                <br />

                                <span className="credit-row d-inline">
                                    <span className="credit-row-left d-inline">
                                        {t("movie.credits.dopAndCoProd")}
                                    </span>
                                    <span className="credit-row-right d-inline">
                                        {" "}
                                        {t("movie.credits.dopAndCoProdName")}
                                    </span>
                                </span>

                                <span className="credit-row d-inline">
                                    <span className="credit-row-left d-inline">
                                        {t("movie.credits.soundDesign")}
                                    </span>
                                    <span className="credit-row-right d-inline">
                                        {" "}
                                        {t("movie.credits.soundDesignName")}
                                    </span>
                                </span>

                                <span className="credit-row d-inline">
                                    <span className="credit-row-left d-inline">
                                        {t("movie.credits.postCoordinator")}
                                    </span>
                                    <span className="credit-row-right d-inline">
                                        {" "}
                                        {t("movie.credits.postCoordinatorName")}
                                    </span>
                                </span>

                                <span className="credit-row d-inline">
                                    <span className="credit-row-left d-inline">
                                        {t("movie.credits.editing")}
                                    </span>
                                    <span className="credit-row-right d-inline">
                                        {" "}
                                        {t("movie.credits.editingName")}
                                    </span>
                                </span>

                                <span className="credit-row d-inline">
                                    <span className="credit-row-left d-inline">
                                        {t("movie.credits.executiveProduction")}
                                    </span>
                                    <span className="credit-row-right d-inline">
                                        {" "}
                                        {t("movie.credits.executiveProductionNames")}
                                    </span>
                                </span>

                                <span className="credit-row d-inline">
                                    <span className="credit-row-left d-inline">
                                        {t("movie.credits.production")}
                                    </span>
                                    <span className="credit-row-right d-inline">
                                        {" "}
                                        {t("movie.credits.productionName")}
                                    </span>
                                </span>

                                <span className="credit-row d-inline">
                                    <span className="credit-row-left d-inline">
                                        {t("movie.credits.writtenAndDirected")}
                                    </span>
                                    <span className="credit-row-right d-inline">
                                        {" "}
                                        {t("movie.credits.writtenAndDirectedName")}
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Credits - Mobile */}
                    <div className="movie-credits-mobile">
                        <PhotoLinerV2
                            title={t("sections.creditsTitle")}
                            color="#ffffff"
                            customClass="fs32 fw700"
                            hovered
                        />
                        <SafeArea>
                            <div className="up tac d-flex flex-col ln26 fw900" style={{ marginTop: 128 }}>
                                <span className="credit-row-left">
                                    {t("movie.credits.withParticipation")}
                                </span>
                                <span className="credit-row-right">{t("movie.credits.cast")}</span>

                                <br />

                                <span className="credit-row d-inline">
                                    <span className="credit-row-left d-inline">
                                        {t("movie.credits.dopAndCoProd")}
                                    </span>
                                    <span className="credit-row-right d-inline">
                                        {" "}
                                        {t("movie.credits.dopAndCoProdName")}
                                    </span>
                                </span>

                                <span className="credit-row d-inline">
                                    <span className="credit-row-left d-inline">
                                        {t("movie.credits.soundDesign")}
                                    </span>
                                    <span className="credit-row-right d-inline">
                                        {" "}
                                        {t("movie.credits.soundDesignName")}
                                    </span>
                                </span>

                                <span className="credit-row d-inline">
                                    <span className="credit-row-left d-inline">
                                        {t("movie.credits.postCoordinator")}
                                    </span>
                                    <span className="credit-row-right d-inline">
                                        {" "}
                                        {t("movie.credits.postCoordinatorName")}
                                    </span>
                                </span>

                                <span className="credit-row d-inline">
                                    <span className="credit-row-left d-inline">{t("movie.credits.editing")}</span>
                                    <span className="credit-row-right d-inline">
                                        {" "}
                                        {t("movie.credits.editingName")}
                                    </span>
                                </span>

                                <span className="credit-row d-inline">
                                    <span className="credit-row-left d-inline">
                                        {t("movie.credits.executiveProduction")}
                                    </span>
                                    <span className="credit-row-right d-inline">
                                        {" "}
                                        {t("movie.credits.executiveProductionNames")}
                                    </span>
                                </span>

                                <span className="credit-row d-inline">
                                    <span className="credit-row-left d-inline">{t("movie.credits.production")}</span>
                                    <span className="credit-row-right d-inline">
                                        {" "}
                                        {t("movie.credits.productionName")}
                                    </span>
                                </span>

                                <span className="credit-row d-inline">
                                    <span className="credit-row-left d-inline">
                                        {t("movie.credits.writtenAndDirected")}
                                    </span>
                                    <span className="credit-row-right d-inline">
                                        {" "}
                                        {t("movie.credits.writtenAndDirectedName")}
                                    </span>
                                </span>
                            </div>
                        </SafeArea>

                        <img
                            className="objcov"
                            src="https://images-extras.s3.us-east-1.amazonaws.com/movie_credits_mobile.png"
                            alt=""
                        />
                    </div>
                </>
            </Wrapper>
        </>
    );
};

export default Movie;
